import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http, money, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Upload, Wand2, CheckCircle2, AlertTriangle, Plus, Trash2, RotateCcw, FileText } from "lucide-react";
import { toast } from "sonner";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const STATUS_LABEL = {
  confirmed: { t: "Confermata", c: "bg-emerald-100 text-emerald-800" },
  difference: { t: "Differenza", c: "bg-amber-100 text-amber-800" },
  only_pdf: { t: "Solo PDF", c: "bg-sky-100 text-sky-800" },
  only_whatsapp: { t: "Solo WhatsApp", c: "bg-violet-100 text-violet-800" },
};

const FILTERS = [
  { k: "all", t: "Tutte" }, { k: "confirmed", t: "Confermate" }, { k: "review", t: "Da verificare" },
  { k: "difference", t: "Differenze" }, { k: "only_pdf", t: "Solo PDF" }, { k: "only_whatsapp", t: "Solo WhatsApp" },
  { k: "conflict", t: "Conflitti" },
];

function Stat({ label, value, tone = "" }) {
  return (
    <div className={`rounded-2xl border p-3 ${tone || "border-slate-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-bold text-[var(--teal-deep)] tabular-nums">{value}</div>
    </div>
  );
}

export default function ImportPulizie() {
  const now = new Date();
  const [tab, setTab] = useState("import");
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [apartments, setApartments] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newRow, setNewRow] = useState({ date: "", apartment_id: "", guests: 0, operator1_name: "", notes: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([http.get("/apartments"), http.get("/cleaners")]);
      setApartments(a.data); setCleaners(c.data);
    })();
  }, []);

  const loadDraft = async () => {
    const { data } = await http.get("/cleaning-imports", { params: { year: Number(year), month: Number(month) } });
    setDraft(data || null);
    if (data?.whatsapp_text) setText(data.whatsapp_text);
  };
  const loadReport = async () => {
    const { data } = await http.get("/cleaning-reports", { params: { year: Number(year), month: Number(month) } });
    setReport(data);
  };
  useEffect(() => { loadDraft(); loadReport(); /* eslint-disable-next-line */ }, [year, month]);

  const analyze = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("year", year); fd.append("month", month); fd.append("whatsapp_text", text);
      if (file) fd.append("file", file);
      const { data } = await http.post("/cleaning-imports/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft(data);
      toast.success(`${data.stats.total} pulizie analizzate · ${data.stats.needs_review} da verificare`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const patchRow = async (rowId, patch) => {
    try {
      const { data } = await http.patch(`/cleaning-imports/${draft.id}/rows/${rowId}`, patch);
      setDraft(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addRow = async () => {
    try {
      const { data } = await http.post(`/cleaning-imports/${draft.id}/rows`, {
        ...newRow, guests: Number(newRow.guests) || 0,
      });
      setDraft(data); setAddOpen(false); setNewRow({ date: "", apartment_id: "", guests: 0, operator1_name: "", notes: "" });
      toast.success("Riga aggiunta alla bozza");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/cleaning-imports/${draft.id}/commit`);
      setDraft(data.import);
      toast.success(`Mese confermato: ${data.created} create, ${data.updated} aggiornate, ${data.kept_existing} mantenute`);
      setConfirmOpen(false); loadReport();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const reopen = async () => {
    await http.post(`/cleaning-months/${year}/${month}/reopen`);
    toast.success("Mese riaperto"); loadDraft(); loadReport();
  };

  const rows = draft?.rows || [];
  const shown = useMemo(() => rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "review") return r.needs_review;
    if (filter === "conflict") return !!r.conflict?.existing_id;
    if (filter === "confirmed") return r.status === "confirmed" && !r.needs_review;
    return r.status === filter;
  }), [rows, filter]);
  const s = draft?.stats;
  const grouped = useMemo(() => {
    const map = new Map();
    shown.forEach((r) => { if (!map.has(r.date)) map.set(r.date, []); map.get(r.date).push(r); });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [shown]);
  const dayLabel = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return `${String(d.getDate()).padStart(2, "0")} ${MESI[d.getMonth()].toUpperCase()}`;
  };
  const dayTotal = (list) => list.reduce((a, r) => a + (r.ignored ? 0 : Number(r.total_cost) || 0), 0);
  const aptTariff = (id) => apartments.find((a) => a.id === id)?.cleaning_cost || 0;

  return (
    <div className="space-y-6" data-testid="import-pulizie-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <Link to="/pulizie" className="text-xs text-slate-500 hover:text-teal-700 inline-flex items-center gap-1" data-testid="back-pulizie">
            <ArrowLeft className="h-3 w-3" /> Registro pulizie
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Importazione mensile</h1>
          <p className="text-sm text-slate-500">PDF del gestionale + messaggio WhatsApp → anteprima controllabile → conferma mese.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={tab === "import" ? "default" : "outline"} onClick={() => setTab("import")} className="rounded-xl" data-testid="tab-import">Importazione</Button>
          <Button variant={tab === "report" ? "default" : "outline"} onClick={() => { setTab("report"); loadReport(); }} className="rounded-xl" data-testid="tab-report">Report mensile</Button>
        </div>
      </div>

      <Card className="p-4 rounded-2xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label>Mese</Label>
            <Select value={month} onValueChange={setMonth}><SelectTrigger data-testid="imp-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MESI.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Anno</Label>
            <Select value={year} onValueChange={setYear}><SelectTrigger data-testid="imp-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            {report?.month_status === "chiuso"
              ? <><Badge className="bg-emerald-600 text-white" data-testid="month-status">{MESI[Number(month) - 1]} {year} — CHIUSO</Badge>
                  <Button variant="outline" size="sm" onClick={reopen} className="rounded-xl" data-testid="reopen-month"><RotateCcw className="h-3 w-3 mr-1" /> Riapri mese</Button></>
              : <Badge variant="outline" data-testid="month-status">{MESI[Number(month) - 1]} {year} — {(report?.month_status || "bozza").replace("_", " ")}</Badge>}
          </div>
        </div>
      </Card>

      {tab === "import" && <>
        <Card className="p-4 rounded-2xl space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>1. PDF planner del gestionale (opzionale)</Label>
              <label className="mt-1 flex items-center gap-2 rounded-xl border border-dashed border-slate-300 p-3 cursor-pointer hover:border-[var(--teal-deep)]">
                <Upload className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 truncate">{file?.name || draft?.pdf_filename || "Scegli un file PDF"}</span>
                <input type="file" accept="application/pdf" className="hidden" data-testid="imp-file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              <p className="text-xs text-slate-400 mt-1">Ogni check-out genera una pulizia nello stesso giorno.</p>
            </div>
            <div>
              <Label>2. Messaggio WhatsApp del mese (opzionale)</Label>
              <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="imp-text"
                placeholder={"17/8 KAKU e Michela\nGhiberti\nSub 51 x4 Kaku\nSISTIANA X6 Michela"} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={analyze} disabled={busy} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]" data-testid="analyze-btn">
              <Wand2 className="h-4 w-4 mr-1" /> {busy ? "Analisi..." : "Analizza e confronta"}
            </Button>
            <span className="text-xs text-slate-500 self-center">L'analisi non scrive nulla nel registro: crea solo una bozza.</span>
          </div>
        </Card>

        {s && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="imp-stats">
              <Stat label="Pulizie trovate" value={s.total} />
              <Stat label="Confermate" value={s.confirmed} tone="border-emerald-200 bg-emerald-50" />
              <Stat label="Differenze" value={s.difference} tone="border-amber-200 bg-amber-50" />
              <Stat label="Solo PDF" value={s.only_pdf} tone="border-sky-200 bg-sky-50" />
              <Stat label="Solo WhatsApp" value={s.only_whatsapp} tone="border-violet-200 bg-violet-50" />
              <Stat label="Da verificare" value={s.needs_review} tone="border-rose-200 bg-rose-50" />
              <Stat label="Costo totale" value={money(s.cost)} />
            </div>
            <div className="text-xs text-slate-500">
              Righe da PDF: {draft.counts_source?.pdf ?? 0} · righe da WhatsApp: {draft.counts_source?.whatsapp ?? 0} · giornate WhatsApp: {draft.counts_source?.whatsapp_days ?? 0}
              {s.conflicts > 0 && <span className="text-rose-700 font-semibold"> · {s.conflicts} conflitti con il registro reale</span>}
              {!draft.has_pdf && <span className="text-amber-700"> · nessun PDF: confronto col planner non effettuato</span>}
              {!draft.has_whatsapp && <span className="text-amber-700"> · nessun testo WhatsApp: usati i default operatrice</span>}
            </div>

            {/* RIEPILOGO IN ALTO */}
            <Card className="p-4 rounded-2xl border-[var(--teal-deep)]/30" data-testid="summary-card">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="font-display font-bold text-[var(--teal-deep)] text-lg">Riepilogo prima della conferma</div>
                  <div className="text-slate-600">
                    <b>{s.total}</b> interventi · <b>{money(s.cost)}</b> costo totale ·
                    <span className={s.needs_review ? "text-rose-700 font-semibold" : ""}> {s.needs_review} da verificare</span> ·
                    <span className={s.conflicts ? "text-rose-700 font-semibold" : ""}> {s.conflicts} conflitti</span>
                  </div>
                </div>
                <Button onClick={() => setConfirmOpen(true)} disabled={busy || !s.total} data-testid="confirm-month-btn"
                  className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]">Conferma mese pulizie</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                <div className="rounded-xl bg-slate-50 p-3" data-testid="summary-apartments">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Per appartamento</div>
                  {draft.breakdown?.per_apartment.map((p) => (
                    <div key={p.apartment_id} className="flex justify-between py-0.5"><span>{p.apartment_name} <span className="text-slate-400">({p.count})</span></span><span className="tabular-nums font-semibold">{money(p.total)}</span></div>
                  ))}
                </div>
                <div className="rounded-xl bg-slate-50 p-3" data-testid="summary-operators">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Per operatrice</div>
                  {draft.breakdown?.per_operator.map((p) => (
                    <div key={p.operator} className="flex justify-between py-0.5"><span>{p.operator} <span className="text-slate-400">({p.count} interventi)</span></span><span className="tabular-nums font-semibold">{money(p.total)}</span></div>
                  ))}
                </div>
              </div>
            </Card>

            <div className="flex gap-2 flex-wrap">
              {FILTERS.map((f) => (
                <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`filter-${f.k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${filter === f.k ? "bg-[var(--teal-deep)] text-white border-[var(--teal-deep)]" : "border-slate-300 text-slate-600 hover:border-[var(--teal-deep)]"}`}>
                  {f.t}
                </button>
              ))}
              <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="rounded-full" data-testid="add-row-btn"><Plus className="h-3 w-3 mr-1" /> Aggiungi pulizia</Button>
            </div>

            {/* GIORNATE RAGGRUPPATE */}
            <div className="space-y-4" data-testid="imp-table">
              {grouped.length === 0 && <Card className="p-10 rounded-2xl text-center text-slate-400">Nessuna riga nel filtro selezionato.</Card>}
              {grouped.map(([day, list]) => (
                <Card key={day} className="rounded-2xl overflow-hidden" data-testid={`imp-day-${day}`}>
                  <div className="flex items-center justify-between px-4 py-2 bg-[var(--teal-deep)] text-white">
                    <div className="font-display font-bold tracking-wide">{dayLabel(day)}</div>
                    <div className="text-xs opacity-90">{list.length} {list.length === 1 ? "intervento" : "interventi"} · {money(dayTotal(list))}</div>
                  </div>
                  {/* desktop */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {list.map((r) => (
                          <tr key={r.row_id} data-testid={`imp-row-${r.row_id}`}
                            className={`border-b border-slate-100 last:border-0 ${r.ignored ? "opacity-40" : ""} ${r.needs_review ? "bg-rose-50/50" : ""}`}>
                            <td className="py-2 px-4 font-medium">{r.apartment_name}</td>
                            <td className="px-2 tabular-nums whitespace-nowrap text-slate-600">{r.guests} ospiti</td>
                            <td className="px-2 whitespace-nowrap">
                              {r.operator1_name || <span className="text-rose-600">—</span>} <span className="text-slate-400 tabular-nums">{money(r.operator1_fee)}</span>
                              {r.operator2_name && <> · {r.operator2_name} <span className="text-slate-400 tabular-nums">{money(r.operator2_fee)}</span></>}
                            </td>
                            <td className="px-2 text-right tabular-nums font-semibold whitespace-nowrap">{money(r.total_cost)}</td>
                            <td className="px-2 text-[10px] text-slate-500 whitespace-nowrap">{r.sources?.pdf ? "PDF ✓" : "PDF —"} · {r.sources?.whatsapp ? "WA ✓" : "WA —"}</td>
                            <td className="px-2">
                              <Badge className={STATUS_LABEL[r.status]?.c}>{STATUS_LABEL[r.status]?.t}</Badge>
                              {r.conflict?.existing_id && <Badge variant="outline" className="ml-1 border-rose-300 text-rose-700 text-[10px]">Già in registro</Badge>}
                              {r.needs_review && <div className="mt-1 text-[10px] text-rose-700">{r.review_reasons?.join(" · ")}</div>}
                              {r.diffs?.length > 0 && <div className="mt-1 text-[10px] text-amber-700">{r.diffs.join(" · ")}</div>}
                            </td>
                            <td className="px-2 text-xs text-slate-500 max-w-[260px]">{r.notes}</td>
                            <td className="px-3">
                              <div className="flex gap-1 justify-end">
                                {r.needs_review && <button onClick={() => patchRow(r.row_id, { resolve_review: true })} title="Segna come controllata"
                                  className="p-1.5 text-emerald-600 hover:text-emerald-800" data-testid={`ok-${r.row_id}`}><CheckCircle2 className="h-4 w-4" /></button>}
                                <button onClick={() => setEditRow({ ...r })} title="Modifica" className="p-1.5 text-slate-500 hover:text-teal-700" data-testid={`edit-${r.row_id}`}>✎</button>
                                <button onClick={() => patchRow(r.row_id, { ignored: !r.ignored })} title="Ignora" className="p-1.5 text-slate-500 hover:text-rose-600" data-testid={`del-${r.row_id}`}><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* mobile / tablet */}
                  <div className="md:hidden divide-y divide-slate-100">
                    {list.map((r) => (
                      <div key={r.row_id} className={`p-3 ${r.needs_review ? "bg-rose-50/50" : ""} ${r.ignored ? "opacity-40" : ""}`} data-testid={`imp-card-${r.row_id}`}>
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <div className="font-semibold text-slate-900">{r.apartment_name}</div>
                            <div className="text-xs text-slate-500">{r.guests} ospiti · {r.operator1_name || "—"}{r.operator2_name ? ` + ${r.operator2_name}` : ""}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold tabular-nums">{money(r.total_cost)}</div>
                            <Badge className={`${STATUS_LABEL[r.status]?.c} mt-1`}>{STATUS_LABEL[r.status]?.t}</Badge>
                          </div>
                        </div>
                        {r.notes && <div className="text-xs text-slate-500 mt-1">{r.notes}</div>}
                        {r.needs_review && <div className="text-[11px] text-rose-700 mt-1">{r.review_reasons?.join(" · ")}</div>}
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline" onClick={() => setEditRow({ ...r })} className="rounded-xl">Modifica</Button>
                          {r.needs_review && <Button size="sm" variant="outline" onClick={() => patchRow(r.row_id, { resolve_review: true })} className="rounded-xl">Controllata</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>

            {(draft.pdf_unparsed?.length > 0 || draft.whatsapp_unparsed?.length > 0) && (
              <Card className="p-4 rounded-2xl" data-testid="unparsed-card">
                <details>
                  <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                    Righe non interpretate ({(draft.pdf_unparsed?.length || 0) + (draft.whatsapp_unparsed?.length || 0)}) — clicca per verificare
                  </summary>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-xs" data-testid="unparsed-table">
                      <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="py-1 pr-3">Fonte</th><th className="pr-3">Testo originale</th><th>Motivo</th>
                      </tr></thead>
                      <tbody>
                        {[...(draft.pdf_unparsed || []), ...(draft.whatsapp_unparsed || [])].map((u, i) => {
                          const row = typeof u === "string" ? { text: u, source: "?", reason: "Non interpretata" } : u;
                          return (
                            <tr key={i} className="border-b border-slate-100 align-top">
                              <td className="py-1 pr-3"><Badge variant="outline" className="text-[10px] uppercase">{row.source === "pdf" ? "PDF" : "WhatsApp"}</Badge></td>
                              <td className="pr-3 font-mono text-[11px] text-slate-700 break-all max-w-[420px]">{row.text}</td>
                              <td className="text-slate-500">{row.reason}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </details>
              </Card>
            )}
          </>
        )}
      </>}

      {tab === "report" && report && (
        <div className="space-y-4" data-testid="report-section">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Interventi mese" value={report.count} />
            <Stat label="Costo operativo" value={money(report.total_cost)} />
            <Stat label="Stato mese" value={(report.month_status || "bozza").replace("_", " ")} />
            <Stat label="Ultima conferma" value={report.confirmed_at ? fmtDate(report.confirmed_at.slice(0, 10)) : "—"} />
          </div>
          <Card className="p-4 rounded-2xl">
            <div className="font-display font-bold text-[var(--teal-deep)] mb-2 flex items-center gap-2"><FileText className="h-4 w-4" /> Report operatrici</div>
            <div className="space-y-3">
              {report.per_operator.length === 0 && <div className="text-sm text-slate-400">Nessun intervento nel mese.</div>}
              {report.per_operator.map((o) => (
                <details key={o.operator} className="rounded-xl border border-slate-200 p-3" data-testid={`op-${o.operator}`}>
                  <summary className="cursor-pointer flex justify-between text-sm">
                    <span className="font-semibold">{o.operator} <span className="text-slate-400">({o.count} interventi)</span></span>
                    <span className="tabular-nums">{money(o.total)} · da pagare <b className="text-amber-700">{money(o.unpaid)}</b></span>
                  </summary>
                  <table className="w-full text-xs mt-2">
                    <tbody>
                      {o.rows.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-1 tabular-nums">{fmtDate(r.date)}</td>
                          <td>{r.apartment_name}</td>
                          <td className="text-right tabular-nums">{money(r.fee)}</td>
                          <td className="text-right">{r.paid ? "Pagato" : "Da pagare"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl">
            <div className="font-display font-bold text-[var(--teal-deep)] mb-2">Report appartamenti · confronto con rendiconto</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-apt-table">
                <thead><tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2">Appartamento</th><th>Interventi</th><th className="text-right">Costo operativo</th>
                  <th className="text-right">Addebitato rendiconto</th><th className="text-right">Differenza</th>
                </tr></thead>
                <tbody>
                  {report.per_apartment.map((p) => (
                    <tr key={p.apartment_id} className="border-b border-slate-100">
                      <td className="py-2">{p.apartment_name}</td>
                      <td className="tabular-nums">{p.count}</td>
                      <td className="text-right tabular-nums">{money(p.operational_cost)}</td>
                      <td className="text-right tabular-nums">{p.report_charged != null ? money(p.report_charged) : "—"}</td>
                      <td className={`text-right tabular-nums ${p.diff > 0 ? "text-emerald-700" : p.diff < 0 ? "text-rose-700" : ""}`}>{p.diff != null ? money(p.diff) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2">Confronto informativo: il rendiconto proprietario resta la fonte economica ufficiale e non viene modificato.</p>
          </Card>
        </div>
      )}

      {/* Modifica riga */}
      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">Modifica riga bozza</DialogTitle></DialogHeader>
          {editRow && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" value={editRow.date} onChange={(e) => setEditRow({ ...editRow, date: e.target.value })} data-testid="edit-date" /></div>
              <div><Label>Appartamento</Label>
                <Select value={editRow.apartment_id} onValueChange={(v) => setEditRow({ ...editRow, apartment_id: v, total_cost: aptTariff(v) })}>
                  <SelectTrigger data-testid="edit-apt"><SelectValue /></SelectTrigger>
                  <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Ospiti</Label><Input type="number" min="0" value={editRow.guests} onChange={(e) => setEditRow({ ...editRow, guests: e.target.value })} data-testid="edit-guests" /></div>
              <div><Label>Totale intervento (€)</Label><Input type="number" step="0.01" value={editRow.total_cost} onChange={(e) => setEditRow({ ...editRow, total_cost: e.target.value })} data-testid="edit-total" /></div>
              <div><Label>Operatrice 1</Label>
                <Select value={editRow.operator1_name || ""} onValueChange={(v) => setEditRow({ ...editRow, operator1_name: v })}>
                  <SelectTrigger data-testid="edit-op1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{cleaners.filter((c) => c.active).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Compenso 1 (€)</Label><Input type="number" step="0.01" value={editRow.operator1_fee} onChange={(e) => setEditRow({ ...editRow, operator1_fee: e.target.value })} data-testid="edit-fee1" /></div>
              <div><Label>Operatrice 2</Label>
                <Select value={editRow.operator2_name || "none"} onValueChange={(v) => setEditRow({ ...editRow, operator2_name: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-op2"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuna</SelectItem>{cleaners.filter((c) => c.active).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Compenso 2 (€)</Label><Input type="number" step="0.01" value={editRow.operator2_fee} onChange={(e) => setEditRow({ ...editRow, operator2_fee: e.target.value })} data-testid="edit-fee2" /></div>
              <div className="md:col-span-2"><Label>Note</Label><Textarea rows={3} value={editRow.notes || ""} onChange={(e) => setEditRow({ ...editRow, notes: e.target.value })} data-testid="edit-notes" /></div>
              {editRow.conflict?.existing_id && (
                <div className="md:col-span-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs space-y-2">
                  <div className="font-semibold text-rose-800 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Pulizia già presente nel registro reale</div>
                  <div>Esistente: {editRow.conflict.existing.operator1_name || "—"} · {money(editRow.conflict.existing.total_cost)} · {editRow.conflict.existing.guests} ospiti {editRow.conflict.existing.paid ? "· pagata" : ""}</div>
                  <div>Importata: {editRow.operator1_name || "—"} · {money(editRow.total_cost)} · {editRow.guests} ospiti</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant={editRow.conflict.resolution === "keep_existing" ? "default" : "outline"} className="rounded-xl"
                      onClick={() => patchRow(editRow.row_id, { conflict_resolution: "keep_existing" }).then(() => setEditRow(null))} data-testid="keep-existing">Mantieni esistente</Button>
                    <Button size="sm" variant={editRow.conflict.resolution === "use_imported" ? "default" : "outline"} className="rounded-xl"
                      onClick={() => patchRow(editRow.row_id, { conflict_resolution: "use_imported" }).then(() => setEditRow(null))} data-testid="use-imported">Usa importata</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Annulla</Button>
            <Button className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="edit-save"
              onClick={async () => {
                const p = {
                  date: editRow.date, apartment_id: editRow.apartment_id, guests: Number(editRow.guests) || 0,
                  operator1_name: editRow.operator1_name || undefined, operator1_fee: Number(editRow.operator1_fee) || 0,
                  operator2_name: editRow.operator2_name || "", operator2_fee: Number(editRow.operator2_fee) || 0,
                  notes: editRow.notes || "", resolve_review: true,
                };
                await patchRow(editRow.row_id, p); setEditRow(null); toast.success("Riga aggiornata");
              }}>Salva riga</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aggiungi riga */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Aggiungi pulizia alla bozza</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Data</Label><Input type="date" value={newRow.date} onChange={(e) => setNewRow({ ...newRow, date: e.target.value })} data-testid="new-date" /></div>
            <div><Label>Appartamento</Label>
              <Select value={newRow.apartment_id} onValueChange={(v) => setNewRow({ ...newRow, apartment_id: v })}>
                <SelectTrigger data-testid="new-apt"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Ospiti</Label><Input type="number" min="0" value={newRow.guests} onChange={(e) => setNewRow({ ...newRow, guests: e.target.value })} data-testid="new-guests" /></div>
            <div><Label>Operatrice</Label>
              <Select value={newRow.operator1_name} onValueChange={(v) => setNewRow({ ...newRow, operator1_name: v })}>
                <SelectTrigger data-testid="new-op1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{cleaners.filter((c) => c.active).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={newRow.notes} onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annulla</Button>
            <Button onClick={addRow} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="new-save">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma mese */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Conferma {MESI[Number(month) - 1]} {year}</DialogTitle></DialogHeader>
          {s && <div className="space-y-2 text-sm">
            <div>{s.total} interventi · costo totale <b>{money(s.cost)}</b></div>
            {draft.breakdown?.per_operator.map((p) => <div key={p.operator} className="flex justify-between"><span>{p.operator} ({p.count})</span><span className="tabular-nums">{money(p.total)}</span></div>)}
            {(s.needs_review > 0 || s.conflicts > 0) && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Ci sono ancora {s.needs_review} righe da verificare e {s.conflicts} conflitti non risolti. I conflitti non risolti mantengono il dato già presente nel registro (nessuna sovrascrittura).</span>
              </div>
            )}
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button onClick={commit} disabled={busy} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="commit-btn">
              {busy ? "Conferma..." : "Conferma e scrivi nel registro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
